/**
 * One-shot login continuation after an inactivity sign-out (#2212).
 *
 * OIDC end-session follows the registered `post_logout_redirect_uri` and
 * cannot carry `?reason=` or a return path. Persist *before* `clearAuth` so a
 * lazy LoginPage that mounts when the guard flips can peek the reason.
 * `clearAuth` still wipes `redirectAfterLogin`; re-publish that after.
 * Storage accessors are try/caught so a private window cannot crash the page.
 */

import { isSafeInternalPath } from '@/stores/authStore';
import { INACTIVITY_LOGOUT_REASON } from './sessionBroadcast';

export const LOGIN_CONTINUATION_STORAGE_KEY = 'staff-portal-login-continuation';

export type LoginContinuation = {
  reason: typeof INACTIVITY_LOGOUT_REASON;
  returnTo: string | null;
};

/**
 * `undefined` = not yet read this JS realm; `null` = read, nothing to show.
 * Survives React Strict Mode remounts after storage has been cleared.
 */
let cached: LoginContinuation | null | undefined;

/**
 * Accessing `sessionStorage` / `localStorage` *as getters* can throw
 * `SecurityError` in a locked-down iframe before any method runs. Guard the
 * accessor, not only getItem/setItem/removeItem.
 */
function storageOf(kind: 'sessionStorage' | 'localStorage'): Storage | null {
  try {
    const storage = globalThis[kind];
    if (storage === undefined || storage === null) return null;
    return storage;
  } catch {
    return null;
  }
}

function readStorageItem(kind: 'sessionStorage' | 'localStorage'): string | null {
  const storage = storageOf(kind);
  if (storage === null) return null;
  try {
    return storage.getItem(LOGIN_CONTINUATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorageItem(kind: 'sessionStorage' | 'localStorage', raw: string): void {
  const storage = storageOf(kind);
  if (storage === null) return;
  try {
    storage.setItem(LOGIN_CONTINUATION_STORAGE_KEY, raw);
  } catch {
    // Private window / blocked site data: memory cache still holds the value.
  }
}

function removeStorageItem(kind: 'sessionStorage' | 'localStorage'): void {
  const storage = storageOf(kind);
  if (storage === null) return;
  try {
    storage.removeItem(LOGIN_CONTINUATION_STORAGE_KEY);
  } catch {
    // Memory cache is the source of truth for this realm.
  }
}

function parseContinuation(raw: string): LoginContinuation | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as { reason?: unknown; returnTo?: unknown };
    if (record.reason !== INACTIVITY_LOGOUT_REASON) return null;
    const returnTo =
      typeof record.returnTo === 'string' && isSafeInternalPath(record.returnTo)
        ? record.returnTo
        : null;
    return { reason: INACTIVITY_LOGOUT_REASON, returnTo };
  } catch {
    return null;
  }
}

function readFromStorage(): LoginContinuation | null {
  const sessionRaw = readStorageItem('sessionStorage');
  if (sessionRaw !== null) {
    const parsed = parseContinuation(sessionRaw);
    if (parsed !== null) return parsed;
  }
  const localRaw = readStorageItem('localStorage');
  if (localRaw === null) return null;
  return parseContinuation(localRaw);
}

function clearStorage(): void {
  removeStorageItem('sessionStorage');
  removeStorageItem('localStorage');
}

export function persistLoginContinuation(input: {
  reason: string;
  returnTo?: string;
}): void {
  if (input.reason !== INACTIVITY_LOGOUT_REASON) return;

  const returnTo =
    input.returnTo !== undefined && isSafeInternalPath(input.returnTo)
      ? input.returnTo
      : null;
  const value: LoginContinuation = { reason: INACTIVITY_LOGOUT_REASON, returnTo };
  cached = value;
  const raw = JSON.stringify(value);
  writeStorageItem('sessionStorage', raw);
  writeStorageItem('localStorage', raw);
}

/**
 * Read without consuming. LoginPage must peek: an interim mount before the
 * Paya ID end-session redirect would otherwise drop the reason, and the
 * provider return would have nothing to show (#2212).
 */
export function peekLoginContinuation(): LoginContinuation | null {
  if (cached === undefined) {
    cached = readFromStorage();
  }
  return cached;
}

/**
 * Read the continuation once per JS realm and drop it from storage so a later
 * full load of `/login` does not keep explaining a finished sign-out.
 *
 * Prefer {@link peekLoginContinuation} on LoginPage. Consume on a successful
 * sign-in (`clearLoginContinuation`) instead of on first paint.
 */
export function takeLoginContinuation(): LoginContinuation | null {
  const value = peekLoginContinuation();
  clearStorage();
  return value;
}

export function clearLoginContinuation(): void {
  cached = undefined;
  clearStorage();
}
